import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Brain, Sparkles, Eye, ArrowRight } from "lucide-react";

const PILLARS = [
  {
    letter: "K",
    title: "Knowledge",
    desc: "Understanding what happened across every call, message and appointment.",
    Icon: Eye,
  },
  {
    letter: "A",
    title: "Assistance",
    desc: "A quiet teammate that helps your team accomplish more — without adding work.",
    Icon: Sparkles,
  },
  {
    letter: "I",
    title: "Intelligence",
    desc: "Insights that drive better decisions and surface the next best action.",
    Icon: Brain,
  },
];

/* KAI teaser — compact version. Sits between Audit and FinalCTA. */
export const KaiSection = () => (
  <section
    id="kai"
    data-testid="kai-section"
    className="section-lifecycle relative overflow-hidden bg-gradient-to-b from-white via-slate-50/60 to-white py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20"
  >
    {/* Ocean ambient — radial cyan/teal glow */}
    <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px]">
      <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(60%_60%_at_50%_0%,rgba(20,184,166,0.16),transparent_70%)]" />
    </div>

    <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
      {/* Header — tighter */}
      <div className="text-center">
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-500" />
          </span>
          The brain inside Clinic OS
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.06 }}
          className="mx-auto mt-5 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl"
        >
          Meet{" "}
          <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
            KAI.
          </span>{" "}
          <span className="text-foreground/80 font-semibold tracking-tight">Knowledge · Assistance · Intelligence.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.12 }}
          className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          The AI layer behind every call, message and appointment — quietly turning your clinic&apos;s noise into the next best action.
        </motion.p>
      </div>

      {/* Compact K-A-I pillar row — single line of mini cards */}
      <div className="mx-auto mt-10 grid max-w-4xl gap-3 sm:mt-12 sm:grid-cols-3">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.letter}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.05 + i * 0.07 }}
            data-testid={`kai-pillar-${p.letter.toLowerCase()}`}
            className="group flex items-center gap-3 rounded-2xl border border-border bg-card/80 px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-teal-400/40 hover:shadow-md"
          >
            <div className="relative flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/15 via-teal-500/10 to-emerald-500/10 ring-1 ring-inset ring-teal-500/20">
              <span
                className="font-display text-2xl font-black leading-none tracking-tight text-transparent"
                style={{ WebkitTextStroke: "1.2px rgba(20,184,166,0.7)" }}
                aria-hidden="true"
              >
                {p.letter}
              </span>
              <p.Icon className="absolute -bottom-1 -right-1 h-3.5 w-3.5 text-teal-500 bg-card rounded-full p-0.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-black tracking-tight">{p.title}</p>
              <p className="text-[11.5px] leading-snug text-muted-foreground line-clamp-2">{p.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-9 flex flex-col items-center justify-center gap-2">
        <Link
          to="/kai"
          data-testid="kai-explore-cta"
          className="group inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-cyan-600 via-teal-600 to-emerald-600 px-6 text-sm font-semibold text-white shadow-lg shadow-teal-500/20 transition-all hover:-translate-y-0.5 hover:shadow-teal-500/30"
        >
          Discover KAI
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
        <p className="text-[11px] text-muted-foreground">
          Live today inside Leads &amp; Inbox · expanding across the platform
        </p>
      </div>
    </div>
  </section>
);
